import { Button } from "components/atoms/Button";
import { Header, Paragraph } from "components/atoms/Typography";
import Modal from "components/molecules/Modal/Modal";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { NONE, PTAC_HEATERS } from "../../Survey.constants";
import {
    buildAirHandlingEquipmentSurveyUIStruct,
    buildCentralPlantSurveyUIStruct,
    buildDomesticHotWaterHeaterSurveyUIStruct,
    buildEquipmentInstallationYearAndSizeStruct,
    buildOtherEquipmentSurveyUIStruct,
    buildRooftopUnitsSurveyUIStruct,
    buildTerminalUnitSurveyUIStruct,
    EQUIPMENT_FIELD_AFFIX_LABELS,
    HEAT_PUMP_EXISTS_LABEL,
    getValidatedEquipmentState,
    surveyValueExists,
} from "../../Survey.helpers";
import BooleanHeaderSelect from "../BooleanHeaderSelect";
import { equipmentKeyToLabelMap } from "./BuildingEquipment.constants";
import {
    CUSTOM_CENTRAL_EQUIPMENT_KEYS,
    DERIVED_READ_ONLY_KEYS,
    OPTIONAL_INFERABLE_KEYS,
    PRIMARY_SURVEY_KEY_FOR_DERIVED_EXISTS,
    TERMINAL_SECTION_EDITABLE_KEY_ORDER,
} from "./SingleFormEquipmentSurveyModal.config";
import "./SingleFormEquipmentSurveyModal.scss";

interface SingleFormEquipmentSurveyModalProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    rooftopUnit: RooftopUnit;
    setRooftopUnit: React.Dispatch<React.SetStateAction<RooftopUnit>>;
    airHandlingEquipment: AirHandlingEquipment;
    setAirHandlingEquipment: React.Dispatch<React.SetStateAction<AirHandlingEquipment>>;
    centralPlantCooler: CentralPlantCooler;
    setCentralPlantCooler: React.Dispatch<React.SetStateAction<CentralPlantCooler>>;
    centralPlantHeater: CentralPlantHeater;
    setCentralPlantHeater: React.Dispatch<React.SetStateAction<CentralPlantHeater>>;
    centralPlantHeatPump: CentralPlantHeatPump;
    setCentralPlantHeatPump: React.Dispatch<React.SetStateAction<CentralPlantHeatPump>>;
    terminalCooler: TerminalCooler;
    setTerminalCooler: React.Dispatch<React.SetStateAction<TerminalCooler>>;
    terminalHeater: TerminalHeater;
    setTerminalHeater: React.Dispatch<React.SetStateAction<TerminalHeater>>;
    terminalHeatPump: HeatPump;
    setTerminalHeatPump: React.Dispatch<React.SetStateAction<HeatPump>>;
    domesticHotWaterHeater: DomesticHotWaterHeater;
    setDomesticHotWaterHeater: React.Dispatch<React.SetStateAction<DomesticHotWaterHeater>>;
    otherEquipment: OtherEquipment;
    setOtherEquipment: React.Dispatch<React.SetStateAction<OtherEquipment>>;
    centralPlantGenericEquipment: CentralPlantGenericEquipment;
    setCentralPlantGenericEquipment: React.Dispatch<React.SetStateAction<CentralPlantGenericEquipment>>;
}

const isCentralHeaterHpTerminalUnit = (unit: string | null | undefined) =>
    unit === "water_loop_heat_pump" || unit === "split_air_source_heat_pump";

const isPtacHeaterUnit = (unit: string | null | undefined) => unit != null && PTAC_HEATERS.includes(unit);

const coolingTerminalAllowsPtacHeaterCoolerSize = (terminalCoolerUnits: string | null | undefined) =>
    terminalCoolerUnits == null || terminalCoolerUnits === "cooling_ptac";

const isFullPtacTerminalStack = (tHeater: TerminalHeater, tCooler: TerminalCooler) =>
    tCooler.terminalCoolerUnits === "cooling_ptac" &&
    surveyValueExists(tHeater.terminalHeaterUnits as string | null) &&
    isPtacHeaterUnit(tHeater.terminalHeaterUnits);

const isMissingRequiredValue = (value: unknown) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "number") return Number.isNaN(value);
    return false;
};

const formatDerivedFieldDisplay = (value: unknown) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string") return equipmentKeyToLabelMap[value] ?? value;
    return String(value);
};

const inferFieldInputType = (type: unknown): "boolean" | "number" | "text" | undefined => {
    if (type === "boolean" || type === "checkbox" || type === "checkboxWithSelect") return "boolean";
    if (type === "number") return "number";
    if (type === "text") return "text";
    return undefined;
};

function numericFieldUnitSuffix(
    key: string,
    stateSlice: object,
    unitsByFieldKey: Record<string, string>
): string | undefined {
    if (key === "centralPlantGenericEquipmentSize") {
        const u = (stateSlice as CentralPlantGenericEquipment).centralPlantGenericEquipmentSizeUnits;
        if (u != null && String(u).trim() !== "") {
            return String(u);
        }
    }
    return unitsByFieldKey[key];
}

type FieldMetadata = {
    labelMap: Record<string, string>;
    inputTypeMap: Record<string, "boolean" | "number" | "text">;
    optionsMap: Record<string, string[]>;
    showMap: Record<string, boolean[]>;
    unitMap: Record<string, string>;
};

const createEmptyFieldMetadata = (): FieldMetadata => ({
    labelMap: {},
    inputTypeMap: {},
    optionsMap: {},
    showMap: {},
    unitMap: {},
});

const appendFieldMetadata = (
    metadata: FieldMetadata,
    item: {
        key: unknown;
        label: string;
        show: boolean;
        type: unknown;
        options?: unknown;
        unit?: unknown;
        items?: Array<{ key: unknown; label?: string; type: unknown; options?: unknown; unit?: unknown }>;
    }
): FieldMetadata => {
    const { key, label, show, type, options } = item;
    const nextMetadata: FieldMetadata = {
        labelMap: { ...metadata.labelMap },
        inputTypeMap: { ...metadata.inputTypeMap },
        optionsMap: { ...metadata.optionsMap },
        showMap: { ...metadata.showMap },
        unitMap: { ...metadata.unitMap },
    };
    if (typeof key === "string") {
        nextMetadata.labelMap[key] = label;
        nextMetadata.showMap[key] = [...(nextMetadata.showMap[key] ?? []), show];
        const mappedType = inferFieldInputType(type);
        if (mappedType) nextMetadata.inputTypeMap[key] = mappedType;
        if (Array.isArray(options)) nextMetadata.optionsMap[key] = options.map((option) => String(option));
        if (typeof item.unit === "string" && item.unit.trim() !== "") {
            nextMetadata.unitMap[key] = item.unit;
        }
    }
    if (type === "checkboxWithSelect") {
        const selectOptions = options as { key: unknown; values: unknown; selectFieldLabel?: unknown } | undefined;
        if (selectOptions && typeof selectOptions.key === "string" && Array.isArray(selectOptions.values)) {
            nextMetadata.optionsMap[selectOptions.key] = selectOptions.values.map((option) => String(option));
            const nestedLabel = selectOptions.selectFieldLabel;
            if (typeof nestedLabel === "string" && nestedLabel.trim() !== "") {
                nextMetadata.labelMap[selectOptions.key] = nestedLabel;
            }
        }
    }
    if (type === "genericEquipment") {
        item.items?.forEach((genericItem) => {
            if (typeof genericItem.key !== "string") return;
            if (typeof genericItem.label === "string" && genericItem.label.trim() !== "") {
                nextMetadata.labelMap[genericItem.key] = genericItem.label;
            }
            const mappedType = inferFieldInputType(genericItem.type);
            if (mappedType) nextMetadata.inputTypeMap[genericItem.key] = mappedType;
            if (Array.isArray(genericItem.options)) {
                nextMetadata.optionsMap[genericItem.key] = genericItem.options.map((option) => String(option));
            }
            if (typeof genericItem.unit === "string" && genericItem.unit.trim() !== "") {
                nextMetadata.unitMap[genericItem.key] = genericItem.unit;
            }
        });
    }
    return nextMetadata;
};

function SingleFormEquipmentSurveyModal({
    open,
    onClose,
    onSave,
    rooftopUnit,
    setRooftopUnit,
    airHandlingEquipment,
    setAirHandlingEquipment,
    centralPlantCooler,
    setCentralPlantCooler,
    centralPlantHeater,
    setCentralPlantHeater,
    centralPlantHeatPump,
    setCentralPlantHeatPump,
    terminalCooler,
    setTerminalCooler,
    terminalHeater,
    setTerminalHeater,
    terminalHeatPump,
    setTerminalHeatPump,
    domesticHotWaterHeater,
    setDomesticHotWaterHeater,
    otherEquipment,
    setOtherEquipment,
    centralPlantGenericEquipment,
    setCentralPlantGenericEquipment,
}: SingleFormEquipmentSurveyModalProps) {
    const derivedComputedValueByKey: Record<string, unknown> = {
        rooftopUnitCoolingType: rooftopUnit.rooftopUnitExists ? "direct_expansion" : "none",
        heatPumpType: isCentralHeaterHpTerminalUnit(centralPlantHeater.centralPlantHeaterTerminalUnits)
            ? centralPlantHeater.centralPlantHeaterTerminalUnits
            : "none",
    };

    const effectiveTerminalHeatPumpType = (t: HeatPump, cpHeater: CentralPlantHeater): string | null => {
        const u = cpHeater.centralPlantHeaterTerminalUnits;
        if (isCentralHeaterHpTerminalUnit(u)) return u;
        if (surveyValueExists(t.heatPumpType)) return t.heatPumpType;
        return null;
    };

    const terminalHeatPumpSizeAndYearApplicable = (t: HeatPump, cpHeater: CentralPlantHeater) =>
        surveyValueExists(effectiveTerminalHeatPumpType(t, cpHeater));

    const prevCentralHeaterTerminalUnitsRef = useRef<string | null>(null);

    useEffect(() => {
        if (!open) {
            prevCentralHeaterTerminalUnitsRef.current = null;
            return;
        }
        const u = centralPlantHeater.centralPlantHeaterTerminalUnits;
        const prevU = prevCentralHeaterTerminalUnitsRef.current;
        const wasHp = isCentralHeaterHpTerminalUnit(prevU);
        const isHp = isCentralHeaterHpTerminalUnit(u);
        prevCentralHeaterTerminalUnitsRef.current = u ?? null;

        if (isHp) {
            setTerminalHeatPump((prev) => {
                if (surveyValueExists(prev.heatPumpType)) return prev;
                return { ...prev, heatPumpType: u, heatPumpExists: true };
            });
            return;
        }
        if (wasHp && !isHp) {
            setTerminalHeatPump((prev) => ({
                ...prev,
                heatPumpType: null,
                heatPumpExists: false,
                heatPumpSize: null,
                heatPumpInstallationYear: null,
            }));
        }
    }, [open, centralPlantHeater.centralPlantHeaterTerminalUnits, setTerminalHeatPump]);

    useEffect(() => {
        if (!open) return;
        if (!isPtacHeaterUnit(terminalHeater.terminalHeaterUnits)) return;

        setTerminalCooler((prev) => {
            let next = prev;

            if (next.terminalCoolerUnits == null) {
                next = { ...next, terminalCoolerUnits: "cooling_ptac" };
            }
            if (next.terminalCoolerUnits !== "cooling_ptac") return next;

            if (next.terminalCoolerSize == null) {
                const seedSize = terminalHeater.terminalHeaterCoolerSize ?? terminalHeater.terminalHeaterSize;
                if (seedSize != null) {
                    next = { ...next, terminalCoolerSize: seedSize };
                }
            }
            if (
                next.terminalCoolerAverageInstallationYear == null &&
                terminalHeater.terminalHeaterAverageInstallationYear != null
            ) {
                next = {
                    ...next,
                    terminalCoolerAverageInstallationYear: terminalHeater.terminalHeaterAverageInstallationYear,
                };
            }
            return next;
        });
    }, [
        open,
        terminalHeater.terminalHeaterUnits,
        terminalHeater.terminalHeaterSize,
        terminalHeater.terminalHeaterCoolerSize,
        terminalHeater.terminalHeaterAverageInstallationYear,
        terminalCooler.terminalCoolerUnits,
        setTerminalCooler,
    ]);

    useEffect(() => {
        if (!open) return;
        if (!isPtacHeaterUnit(terminalHeater.terminalHeaterUnits)) return;
        if (terminalHeater.terminalHeaterCoolerSize != null) return;
        if (terminalHeater.terminalHeaterSize == null) return;

        setTerminalHeater((prev) => ({
            ...prev,
            terminalHeaterCoolerSize: prev.terminalHeaterCoolerSize ?? prev.terminalHeaterSize,
        }));
    }, [
        open,
        terminalHeater.terminalHeaterUnits,
        terminalHeater.terminalHeaterSize,
        terminalHeater.terminalHeaterCoolerSize,
        setTerminalHeater,
    ]);

    useEffect(() => {
        if (!open) return;
        if (coolingTerminalAllowsPtacHeaterCoolerSize(terminalCooler.terminalCoolerUnits)) return;
        if (terminalHeater.terminalHeaterCoolerSize == null) return;

        setTerminalHeater((prev) => ({ ...prev, terminalHeaterCoolerSize: null }));
    }, [open, terminalCooler.terminalCoolerUnits, terminalHeater.terminalHeaterCoolerSize, setTerminalHeater]);

    useEffect(() => {
        if (!open) return;
        if (terminalCooler.terminalCoolerUnits == null || terminalCooler.terminalCoolerUnits === "cooling_ptac") return;
        if (!isPtacHeaterUnit(terminalHeater.terminalHeaterUnits)) return;

        setTerminalHeater((prev) => ({
            ...prev,
            terminalHeaterUnits: null,
            terminalHeaterSize: null,
            terminalHeaterAverageInstallationYear: null,
            terminalHeaterCoolerSize: null,
        }));
    }, [open, terminalCooler.terminalCoolerUnits, terminalHeater.terminalHeaterUnits, setTerminalHeater]);

    useEffect(() => {
        if (!open) return;
        if (terminalCooler.terminalCoolerUnits !== "cooling_ptac") return;
        if (terminalHeater.terminalHeaterUnits == null) return;
        if (terminalHeater.terminalHeaterUnits === NONE) return;
        if (isPtacHeaterUnit(terminalHeater.terminalHeaterUnits)) return;

        setTerminalHeater((prev) => ({
            ...prev,
            terminalHeaterUnits: NONE,
            terminalHeaterSize: null,
            terminalHeaterAverageInstallationYear: null,
            terminalHeaterCoolerSize: null,
        }));
    }, [open, terminalCooler.terminalCoolerUnits, terminalHeater.terminalHeaterUnits, setTerminalHeater]);

    const derivedRowDisplayValue = (state: object, key: string, raw: unknown) => {
        if (key === "heatPumpType" && state === terminalHeatPump) {
            const eff = effectiveTerminalHeatPumpType(state as HeatPump, centralPlantHeater);
            return formatDerivedFieldDisplay(eff);
        }
        if (key in derivedComputedValueByKey) {
            return formatDerivedFieldDisplay(derivedComputedValueByKey[key]);
        }
        const pk = PRIMARY_SURVEY_KEY_FOR_DERIVED_EXISTS[key];
        if (pk) {
            const v = (state as Record<string, unknown>)[pk];
            return formatDerivedFieldDisplay(surveyValueExists(v as string | null));
        }
        return formatDerivedFieldDisplay(raw);
    };

    const centralPlantState = {
        ...centralPlantCooler,
        ...centralPlantHeater,
        ...centralPlantHeatPump,
        ...centralPlantGenericEquipment,
        centralPlantExists:
            surveyValueExists(centralPlantCooler.centralPlantCoolerType) ||
            surveyValueExists(centralPlantHeater.centralPlantHeaterType) ||
            surveyValueExists(centralPlantHeatPump.centralPlantHeatPumpType) ||
            surveyValueExists(centralPlantGenericEquipment.centralPlantGenericEquipmentName),
    };

    const fieldMetadata = useMemo(() => {
        let metadata = createEmptyFieldMetadata();
        const appendSurveyStruct = (
            items: Array<{
                key: unknown;
                label: string;
                show: boolean;
                type: unknown;
                options?: unknown;
                items?: Array<{ key: unknown; label?: string; type: unknown; options?: unknown }>;
            }>
        ) =>
            items.forEach((item) => {
                metadata = appendFieldMetadata(metadata, item);
            });

        appendSurveyStruct(buildRooftopUnitsSurveyUIStruct(rooftopUnit, airHandlingEquipment));
        appendSurveyStruct(buildAirHandlingEquipmentSurveyUIStruct(airHandlingEquipment, rooftopUnit));
        appendSurveyStruct(buildCentralPlantSurveyUIStruct(centralPlantState, airHandlingEquipment));
        appendSurveyStruct(
            buildTerminalUnitSurveyUIStruct(
                airHandlingEquipment,
                centralPlantHeater,
                centralPlantCooler,
                terminalCooler,
                terminalHeater,
                rooftopUnit
            )
        );
        appendSurveyStruct(buildDomesticHotWaterHeaterSurveyUIStruct(domesticHotWaterHeater, centralPlantHeater));
        appendSurveyStruct(buildOtherEquipmentSurveyUIStruct());

        const sizeAndYearStruct = buildEquipmentInstallationYearAndSizeStruct(
            rooftopUnit,
            airHandlingEquipment,
            centralPlantCooler,
            centralPlantHeater,
            centralPlantHeatPump,
            terminalCooler,
            terminalHeater,
            terminalHeatPump,
            domesticHotWaterHeater,
            otherEquipment
        );
        Object.values(sizeAndYearStruct).forEach((section) =>
            section.items.forEach(
                (item: {
                    key: unknown;
                    label: string;
                    show?: boolean;
                    type?: unknown;
                    options?: unknown;
                    unit?: unknown;
                }) => {
                    metadata = appendFieldMetadata(metadata, {
                        ...item,
                        show: section.show && item.show !== false,
                        type: item.type ?? "text",
                    });
                }
            )
        );
        metadata = appendFieldMetadata(metadata, {
            key: "heatPumpExists",
            label: HEAT_PUMP_EXISTS_LABEL,
            show: false,
            type: "label",
        });
        return metadata;
    }, [
        rooftopUnit,
        airHandlingEquipment,
        centralPlantState,
        centralPlantHeater,
        centralPlantCooler,
        centralPlantHeatPump,
        terminalCooler,
        terminalHeater,
        terminalHeatPump,
        domesticHotWaterHeater,
        otherEquipment,
    ]);

    const {
        labelMap: fieldLabelMap,
        inputTypeMap: fieldInputTypeMap,
        optionsMap: fieldOptionsMap,
        showMap: fieldShowMap,
        unitMap: fieldUnitMap,
    } = fieldMetadata;

    const labelForFieldKey = (key: string) => fieldLabelMap[key] ?? EQUIPMENT_FIELD_AFFIX_LABELS[key] ?? key;
    const rowLabelForField = (key: string) => labelForFieldKey(key);

    const isBooleanField = (key: string, value: unknown) =>
        fieldInputTypeMap[key] === "boolean" || typeof value === "boolean";

    const isNumericField = (key: string, value: unknown) =>
        fieldInputTypeMap[key] === "number" || typeof value === "number";

    const buildExplicitApplicabilityByKey = (
        overrides: Partial<{
            rooftopUnit: RooftopUnit;
            airHandlingEquipment: AirHandlingEquipment;
            centralPlantCooler: CentralPlantCooler;
            centralPlantHeater: CentralPlantHeater;
            centralPlantHeatPump: CentralPlantHeatPump;
            centralPlantGenericEquipment: CentralPlantGenericEquipment;
            terminalCooler: TerminalCooler;
            terminalHeater: TerminalHeater;
            terminalHeatPump: HeatPump;
            domesticHotWaterHeater: DomesticHotWaterHeater;
            otherEquipment: OtherEquipment;
        }> = {}
    ): Record<string, boolean> => {
        const rooftop = overrides.rooftopUnit ?? rooftopUnit;
        const air = overrides.airHandlingEquipment ?? airHandlingEquipment;
        const cpCooler = overrides.centralPlantCooler ?? centralPlantCooler;
        const cpHeater = overrides.centralPlantHeater ?? centralPlantHeater;
        const cpHeatPump = overrides.centralPlantHeatPump ?? centralPlantHeatPump;
        const cpGeneric = overrides.centralPlantGenericEquipment ?? centralPlantGenericEquipment;
        const tCooler = overrides.terminalCooler ?? terminalCooler;
        const tHeater = overrides.terminalHeater ?? terminalHeater;
        const tHeatPump = overrides.terminalHeatPump ?? terminalHeatPump;
        const dhw = overrides.domesticHotWaterHeater ?? domesticHotWaterHeater;
        const other = overrides.otherEquipment ?? otherEquipment;

        const effectiveAirHandlingEquipmentExists =
            rooftop.rooftopUnitExists === false || air.airHandlingEquipmentExists === true;

        return {
            airHandlingEquipmentExists: rooftop.rooftopUnitExists !== false,
            rooftopUnitHeatingType: rooftop.rooftopUnitExists === true,
            rooftopUnitCoolingType: rooftop.rooftopUnitExists === true,
            rooftopUnitSupplyAirRate: rooftop.rooftopUnitExists === true,
            rooftopUnitAverageInstallationYear: rooftop.rooftopUnitExists === true,
            airHandlingEquipmentType: effectiveAirHandlingEquipmentExists,
            airHandlingEquipmentHeatingType: effectiveAirHandlingEquipmentExists,
            airHandlingEquipmentCoolingType: effectiveAirHandlingEquipmentExists,
            airHandlingEquipmentSupplyAirRate: effectiveAirHandlingEquipmentExists,
            airHandlingEquipmentAverageInstallationYear: effectiveAirHandlingEquipmentExists,
            centralPlantHeaterTerminalUnits:
                surveyValueExists(cpHeater.centralPlantHeaterType) ||
                surveyValueExists(cpHeatPump.centralPlantHeatPumpType) ||
                (cpHeater.centralPlantHeaterType === NONE && cpHeatPump.centralPlantHeatPumpType === NONE),
            centralPlantHeaterSize: surveyValueExists(cpHeater.centralPlantHeaterType),
            centralPlantHeaterAverageInstallationYear: surveyValueExists(cpHeater.centralPlantHeaterType),
            centralPlantCoolerTerminalUnits: surveyValueExists(cpCooler.centralPlantCoolerType),
            centralPlantCoolerSize: surveyValueExists(cpCooler.centralPlantCoolerType),
            centralPlantCoolerAverageInstallationYear: surveyValueExists(cpCooler.centralPlantCoolerType),
            centralPlantHeatPumpSize: surveyValueExists(cpHeatPump.centralPlantHeatPumpType),
            centralPlantHeatPumpAverageInstallationYear: surveyValueExists(cpHeatPump.centralPlantHeatPumpType),
            centralPlantGenericEquipmentSize: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            centralPlantGenericEquipmentSizeUnits: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            centralPlantGenericEquipmentLoadRatio: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            centralPlantGenericEquipmentCoefficientOfPerformance: surveyValueExists(
                cpGeneric.centralPlantGenericEquipmentName
            ),
            centralPlantGenericEquipmentFuel: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            centralPlantGenericEquipmentEndUse: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            centralPlantGenericEquipmentInstallationYear: surveyValueExists(cpGeneric.centralPlantGenericEquipmentName),
            terminalHeaterSize: surveyValueExists(tHeater.terminalHeaterUnits),
            terminalHeaterCoolerSize:
                surveyValueExists(tHeater.terminalHeaterUnits) &&
                isPtacHeaterUnit(tHeater.terminalHeaterUnits) &&
                coolingTerminalAllowsPtacHeaterCoolerSize(tCooler.terminalCoolerUnits),
            terminalHeaterAverageInstallationYear: surveyValueExists(tHeater.terminalHeaterUnits),
            terminalCoolerSize:
                surveyValueExists(tCooler.terminalCoolerUnits) && !isFullPtacTerminalStack(tHeater, tCooler),
            terminalCoolerAverageInstallationYear:
                surveyValueExists(tCooler.terminalCoolerUnits) && !isPtacHeaterUnit(tHeater.terminalHeaterUnits),
            heatPumpSize: terminalHeatPumpSizeAndYearApplicable(tHeatPump, cpHeater),
            heatPumpInstallationYear: terminalHeatPumpSizeAndYearApplicable(tHeatPump, cpHeater),
            domesticHotWaterHeaterType: dhw.domesticHotWaterHeaterExists === true,
            domesticHotWaterHeaterCentralDistribution: dhw.domesticHotWaterHeaterExists === true,
            domesticHotWaterHeaterSize: dhw.domesticHotWaterHeaterExists === true,
            domesticHotWaterHeaterAverageInstallationYear: dhw.domesticHotWaterHeaterExists === true,
            clothesDryersType: other.clothesDryersExists === true,
            clothesDryersEnergyDensity: other.clothesDryersExists === true,
            clothesDryersAverageInstallationYear: other.clothesDryersExists === true,
            clothesWashersEnergyDensity: other.clothesWashersExists === true,
            clothesWashersAverageInstallationYear: other.clothesWashersExists === true,
            elevatorsQuantity: other.elevatorsExists === true,
            elevatorsAverageInstallationYear: other.elevatorsExists === true,
            rooftopPhotovoltaicsSize: other.rooftopPhotovoltaicsExists === true,
            rooftopPhotovoltaicsAverageInstallationYear: other.rooftopPhotovoltaicsExists === true,
            escalatorQuantity: other.escalatorExists === true,
            escalatorInstallationYear: other.escalatorExists === true,
        };
    };
    const explicitApplicabilityByKey = buildExplicitApplicabilityByKey();
    const fieldIsApplicable = (key: string, applicabilityMap: Record<string, boolean> = explicitApplicabilityByKey) => {
        const explicitApplicability = applicabilityMap[key];
        if (explicitApplicability !== undefined) return explicitApplicability;
        const rowEnabled =
            fieldShowMap[key] == null || fieldShowMap[key].length === 0
                ? true
                : fieldShowMap[key].some((showValue) => showValue);
        return rowEnabled;
    };

    const renderSectionRows = <T extends object>(
        sectionKey: string,
        state: T,
        setState: React.Dispatch<React.SetStateAction<T>>,
        options?: { includeDerived?: boolean }
    ) => {
        const includeDerived = options?.includeDerived ?? true;
        const applyValidatedUpdate = (updater: (prev: T) => T) => {
            setState((prev) => {
                const next = updater(prev);
                const explicitApplicabilityForValidation = buildExplicitApplicabilityByKey({
                    rooftopUnit: state === rooftopUnit ? (next as RooftopUnit) : undefined,
                    airHandlingEquipment: state === airHandlingEquipment ? (next as AirHandlingEquipment) : undefined,
                    centralPlantCooler: state === centralPlantCooler ? (next as CentralPlantCooler) : undefined,
                    centralPlantHeater: state === centralPlantHeater ? (next as CentralPlantHeater) : undefined,
                    centralPlantHeatPump: state === centralPlantHeatPump ? (next as CentralPlantHeatPump) : undefined,
                    centralPlantGenericEquipment:
                        state === centralPlantGenericEquipment ? (next as CentralPlantGenericEquipment) : undefined,
                    terminalCooler: state === terminalCooler ? (next as TerminalCooler) : undefined,
                    terminalHeater: state === terminalHeater ? (next as TerminalHeater) : undefined,
                    terminalHeatPump: state === terminalHeatPump ? (next as HeatPump) : undefined,
                    domesticHotWaterHeater:
                        state === domesticHotWaterHeater ? (next as DomesticHotWaterHeater) : undefined,
                    otherEquipment: state === otherEquipment ? (next as OtherEquipment) : undefined,
                });
                const struct = Object.entries(next)
                    .filter(([fieldKey]) => !DERIVED_READ_ONLY_KEYS.has(fieldKey))
                    .map(([fieldKey, fieldValue]) => {
                        const options = fieldOptionsMap[fieldKey];
                        const isApplicable = fieldIsApplicable(fieldKey, explicitApplicabilityForValidation);

                        let structType: "select" | "boolean" | "text" = "text";
                        if (options !== undefined) {
                            structType = "select";
                        } else if (fieldInputTypeMap[fieldKey] === "boolean" || typeof fieldValue === "boolean") {
                            structType = "boolean";
                        }

                        return {
                            label: labelForFieldKey(fieldKey),
                            key: fieldKey as keyof T,
                            show: isApplicable,
                            type: structType,
                            options,
                        };
                    });

                return getValidatedEquipmentState(next, struct as any);
            });
        };

        const editableEntries = Object.entries(state).filter(([key]) => {
            if (DERIVED_READ_ONLY_KEYS.has(key)) return false;
            return true;
        });
        const orderedEditableEntries = (() => {
            const preferredOrder = TERMINAL_SECTION_EDITABLE_KEY_ORDER[sectionKey];
            if (!preferredOrder) return editableEntries;
            const orderIndexByKey = new Map(preferredOrder.map((fieldKey, idx) => [fieldKey, idx]));
            return [...editableEntries]
                .filter(([key]) => {
                    if (sectionKey !== "Terminal cooling units" || key !== "terminalCoolerSize") return true;
                    return !isFullPtacTerminalStack(terminalHeater, terminalCooler);
                })
                .sort(([a], [b]) => {
                    const idxA = orderIndexByKey.get(a);
                    const idxB = orderIndexByKey.get(b);
                    if (idxA == null && idxB == null) return 0;
                    if (idxA == null) return 1;
                    if (idxB == null) return -1;
                    return idxA - idxB;
                });
        })();
        const derivedEntries = Object.entries(state).filter(([key]) => {
            if (!DERIVED_READ_ONLY_KEYS.has(key)) return false;
            if (state === terminalHeatPump && key === "heatPumpType") {
                return (
                    isCentralHeaterHpTerminalUnit(centralPlantHeater.centralPlantHeaterTerminalUnits) ||
                    (state as HeatPump).heatPumpExists === true
                );
            }
            return true;
        });

        const renderEditableInput = (key: string, value: unknown, isApplicable: boolean, stateForUnit: T) => {
            const isBoolean = isBooleanField(key, value);
            const isNumber = isNumericField(key, value);
            const dropdownOptions = fieldOptionsMap[key];
            const controlValue = value == null || typeof value === "object" ? "" : (value as string | number);
            const unitLabel =
                isNumber && dropdownOptions === undefined
                    ? numericFieldUnitSuffix(key, stateForUnit, fieldUnitMap)
                    : undefined;

            if (isBoolean) {
                return (
                    <BooleanHeaderSelect
                        currentValue={value == null ? undefined : Boolean(value)}
                        disabled={!isApplicable}
                        onSelectYes={() => {
                            if (!isApplicable) return;
                            applyValidatedUpdate((prev) => ({
                                ...prev,
                                [key]: true,
                            }));
                        }}
                        onSelectNo={() => {
                            if (!isApplicable) return;
                            applyValidatedUpdate((prev) => ({
                                ...prev,
                                [key]: false,
                            }));
                            if (key === "rooftopUnitExists") {
                                setAirHandlingEquipment((prev) => ({
                                    ...prev,
                                    airHandlingEquipmentExists: true,
                                }));
                            }
                        }}
                    />
                );
            }

            if (dropdownOptions) {
                return (
                    <select
                        value={controlValue}
                        disabled={!isApplicable}
                        onChange={(event) => {
                            if (!isApplicable) return;
                            const inputValue = event.target.value;
                            applyValidatedUpdate((prev) => ({
                                ...prev,
                                [key]: inputValue === "" ? null : inputValue,
                            }));
                        }}
                    >
                        <option value="">--</option>
                        {dropdownOptions.map((optionValue) => (
                            <option key={optionValue} value={optionValue}>
                                {equipmentKeyToLabelMap[optionValue] ?? optionValue}
                            </option>
                        ))}
                    </select>
                );
            }

            const textOrNumberInput = (
                <input
                    type={isNumber ? "number" : "text"}
                    value={controlValue}
                    disabled={!isApplicable}
                    onChange={(event) => {
                        if (!isApplicable) return;
                        const inputValue = event.target.value;
                        let parsedValue: string | number | null = inputValue;
                        if (inputValue === "") {
                            parsedValue = null;
                        } else if (isNumber) {
                            parsedValue = Number(inputValue);
                        }
                        applyValidatedUpdate((prev) => ({
                            ...prev,
                            [key]: parsedValue,
                        }));
                    }}
                />
            );

            if (unitLabel) {
                return (
                    <div className="single-form-equipment-survey-modal__input-with-unit">
                        {textOrNumberInput}
                        <span className="single-form-equipment-survey-modal__unit-suffix">{unitLabel}</span>
                    </div>
                );
            }

            return textOrNumberInput;
        };

        return (
            <>
                {orderedEditableEntries.map(([key, value]) => {
                    const isApplicable = fieldIsApplicable(key);
                    return (
                        <Fragment key={`${sectionKey}-${key}`}>
                            <div
                                className={`single-form-equipment-survey-modal__row ${
                                    !isApplicable ? "single-form-equipment-survey-modal__row--disabled" : ""
                                }`}
                            >
                                <Paragraph size="small">{rowLabelForField(key)}</Paragraph>
                                <div className="single-form-equipment-survey-modal__input">
                                    {renderEditableInput(key, value, isApplicable, state)}
                                </div>
                            </div>
                        </Fragment>
                    );
                })}
                {includeDerived &&
                    derivedEntries.map(([key, value]) => (
                        <div
                            key={`${sectionKey}-${key}-derived`}
                            className="single-form-equipment-survey-modal__row single-form-equipment-survey-modal__row--derived"
                        >
                            <Paragraph size="small">{rowLabelForField(key)}</Paragraph>
                            <div className="single-form-equipment-survey-modal__derived-value">
                                {derivedRowDisplayValue(state, key, value)}
                            </div>
                        </div>
                    ))}
            </>
        );
    };

    const renderSection = <T extends object>(
        sectionTitle: string,
        state: T,
        setState: React.Dispatch<React.SetStateAction<T>>
    ) => (
        <div className="building-equipment-survey-view__section" key={sectionTitle}>
            <Header size="small">{sectionTitle}</Header>
            {renderSectionRows(sectionTitle, state, setState)}
        </div>
    );

    const renderTerminalDerivedRows = () => {
        const terminalStates: Array<{ sectionKey: string; state: object }> = [
            { sectionKey: "Terminal cooling units", state: terminalCooler },
            { sectionKey: "Terminal heating units", state: terminalHeater },
            { sectionKey: "Heat pump terminal units", state: terminalHeatPump },
        ];

        return (
            <>
                {terminalStates.flatMap(({ sectionKey, state }) =>
                    Object.entries(state)
                        .filter(([key]) => {
                            if (!DERIVED_READ_ONLY_KEYS.has(key)) return false;
                            if (state === terminalHeatPump && key === "heatPumpType") {
                                return (
                                    isCentralHeaterHpTerminalUnit(centralPlantHeater.centralPlantHeaterTerminalUnits) ||
                                    terminalHeatPump.heatPumpExists === true
                                );
                            }
                            return true;
                        })
                        .map(([key, value]) => (
                            <div
                                key={`${sectionKey}-${key}-terminal-group-derived`}
                                className="single-form-equipment-survey-modal__row single-form-equipment-survey-modal__row--derived"
                            >
                                <Paragraph size="small">{rowLabelForField(key)}</Paragraph>
                                <div className="single-form-equipment-survey-modal__derived-value">
                                    {derivedRowDisplayValue(state, key, value)}
                                </div>
                            </div>
                        ))
                )}
            </>
        );
    };

    const sectionHasMissingApplicableFields = <T extends object>(state: T) =>
        Object.entries(state).some(([key, value]) => {
            if (DERIVED_READ_ONLY_KEYS.has(key)) return false;
            if (OPTIONAL_INFERABLE_KEYS.has(key)) return false;

            const isApplicable = fieldIsApplicable(key);
            if (!isApplicable) return false;

            return isMissingRequiredValue(value);
        });

    const customEquipmentCompletenessIsInvalid = (() => {
        const applicableValues = CUSTOM_CENTRAL_EQUIPMENT_KEYS.filter((key) => {
            const k = key as string;
            return fieldIsApplicable(k);
        }).map((key) => centralPlantGenericEquipment[key]);

        if (applicableValues.length === 0) return false;

        const missingCount = applicableValues.filter((value) => isMissingRequiredValue(value)).length;
        return missingCount > 0 && missingCount < applicableValues.length;
    })();

    const isSaveDisabled =
        sectionHasMissingApplicableFields(rooftopUnit) ||
        sectionHasMissingApplicableFields(airHandlingEquipment) ||
        sectionHasMissingApplicableFields(centralPlantCooler) ||
        sectionHasMissingApplicableFields(centralPlantHeater) ||
        sectionHasMissingApplicableFields(centralPlantHeatPump) ||
        customEquipmentCompletenessIsInvalid ||
        sectionHasMissingApplicableFields(terminalCooler) ||
        sectionHasMissingApplicableFields(terminalHeater) ||
        sectionHasMissingApplicableFields(terminalHeatPump) ||
        sectionHasMissingApplicableFields(domesticHotWaterHeater) ||
        sectionHasMissingApplicableFields(otherEquipment);

    const footer = (
        <div className="single-form-equipment-survey-modal__footer">
            <Button type="secondary" onClick={onClose}>
                Cancel
            </Button>
            <Button type="primary" onClick={onSave} dataTestId="single-form-survey-save" disabled={isSaveDisabled}>
                Save
            </Button>
        </div>
    );

    return (
        <Modal open={open} onCancel={onClose} title="Equipment Form" footer={footer} size="lg" blockLayout scrollBody>
            <div className="building-equipment-survey-view single-form-equipment-survey-modal__content">
                {renderSection("Packaged Rooftop Units", rooftopUnit, setRooftopUnit)}
                {renderSection("Air Handling Equipment", airHandlingEquipment, setAirHandlingEquipment)}
                {renderSection("Central Plant Cooler", centralPlantCooler, setCentralPlantCooler)}
                {renderSection("Central Plant Heater", centralPlantHeater, setCentralPlantHeater)}
                {renderSection("Central Plant Heat Pump", centralPlantHeatPump, setCentralPlantHeatPump)}
                {renderSection(
                    "Central Plant Custom Equipment",
                    centralPlantGenericEquipment,
                    setCentralPlantGenericEquipment
                )}
                <div className="building-equipment-survey-view__section" key="terminal-units">
                    <Header size="small">Terminal Units</Header>
                    {renderSectionRows("Terminal cooling units", terminalCooler, setTerminalCooler, {
                        includeDerived: false,
                    })}
                    {renderSectionRows("Terminal heating units", terminalHeater, setTerminalHeater, {
                        includeDerived: false,
                    })}
                    {renderSectionRows("Heat pump terminal units", terminalHeatPump, setTerminalHeatPump, {
                        includeDerived: false,
                    })}
                    {renderTerminalDerivedRows()}
                </div>
                {renderSection("Domestic Hot Water Heater", domesticHotWaterHeater, setDomesticHotWaterHeater)}
                {renderSection("Additional Equipment", otherEquipment, setOtherEquipment)}
            </div>
        </Modal>
    );
}

export default SingleFormEquipmentSurveyModal;
